import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { PeriodsPageView } from './PeriodsPageView';
import { usePeriodsPage } from './usePeriodsPage';

/**
 * Periods & year-end close: the fiscal year's monthly periods with their
 * statuses, the per-period close/reopen actions, and the two consequential
 * year-end steps (the CLOSING entry and the retained-earnings rollover), each
 * previewed before it is posted.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child, since React does not catch a component's own throws (the
 * repo's self-served page pattern).
 */
export const PeriodsPage: React.FC = () => {
    const model = usePeriodsPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('periods.error.title'),
            errorMessage: t('periods.error.message'),
            errorRetry: t('periods.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('periods-page')}
        >
            <ErrorBoundary
                data-testid="periods-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="periods-error-boundary"
                onRetry={model.onRetry}
                resetKeys={[model.loadError]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <PeriodsPageContent model={model} />
            </ErrorBoundary>
        </div>
    );
};

type PeriodsPageContentProps = {
    model: ReturnType<typeof usePeriodsPage>;
};

const PeriodsPageContent: React.FC<PeriodsPageContentProps> = ({ model }) => {
    if (model.loadError) {
        throw model.loadError;
    }

    return <PeriodsPageView model={model} />;
};
